/**
 * Transform box math (W2-03 note-lane; DESIGN.md §5.4 "Transform box"; U§5 B5).
 *
 * With two or more selected notes in a lane, a dashed box surrounds them (laneDraw.drawTransformBox):
 *  - body: drag = move in time and X (same as dragging a note);
 *  - top / bottom edge: stretch or squeeze time around the opposite edge (Ctrl / Cmd: around the playhead);
 *  - left / right edge: scale X around the opposite edge (Ctrl / Cmd: around X 0).
 * Time scales are exact rationals p/q (Core.transform.transform), built from the snapped edge beat; X scales are
 * plain numbers, from the snapped edge X. Selected keyframes of other columns follow the time stretch (a phrase of
 * notes plus line motion moves as one); X operations touch notes only. The numeric "Transform..." dialog
 * (DIALOG.transform, W2-11) offers the same with typed values.
 */

import { Beats } from '../../core/time/beat';
import type { Beat } from '../../core/time/types';
import type { TransformSpec } from '../../core/commands/types';
import type { NoteId } from '../../core/model/types';
import type { LaneNoteIndex } from '../../render/timeline/laneHit';
import type { TransformBoxModel } from '../../render/timeline/laneColumn';
import type { TransformHandle } from '../../render/timeline/laneDraw';

/** Box of the selected notes of a lane: beats (start of the earliest, end of the latest) and X extent. */
export interface NoteBox {
  readonly b0: number;
  readonly b1: number;
  readonly x0: number;
  readonly x1: number;
  /** Exact beats of the box edges. */
  readonly t0: Beat;
  readonly t1: Beat;
  readonly count: number;
}

/** The box of the selected notes in an index (null with fewer than `min` of them). */
export function noteBox(
  index: LaneNoteIndex,
  selected: Iterable<NoteId>,
  beatOf: (id: NoteId) => { readonly t: Beat; readonly end: Beat } | null,
  min = 2,
): NoteBox | null {
  let count = 0;
  let x0 = Number.POSITIVE_INFINITY;
  let x1 = Number.NEGATIVE_INFINITY;
  let t0: Beat | null = null;
  let t1: Beat | null = null;
  for (const id of selected) {
    const i = index.indexOf(id);
    if (i < 0) continue;
    const b = beatOf(id);
    if (b === null) continue;
    count++;
    const x = index.x[i] ?? 0;
    x0 = Math.min(x0, x);
    x1 = Math.max(x1, x);
    if (t0 === null || Beats.lt(b.t, t0)) t0 = b.t;
    if (t1 === null || Beats.lt(t1, b.end)) t1 = b.end;
  }
  if (count < min || t0 === null || t1 === null) return null;
  return { b0: Beats.toF64(t0), b1: Beats.toF64(t1), x0, x1, t0, t1, count };
}

/** Box model for drawing (with the active handle). */
export function boxModel(box: NoteBox, active: TransformHandle | null): TransformBoxModel {
  return { b0: box.b0, b1: box.b1, x0: box.x0, x1: box.x1, active };
}

function gcd(a: number, b: number): number {
  let x = Math.abs(a);
  let y = Math.abs(b);
  while (y !== 0) {
    const t = x % y;
    x = y;
    y = t;
  }
  return x === 0 ? 1 : x;
}

/** Beat as an integer fraction num/den (den > 0). */
function frac(b: Beat): readonly [number, number] {
  return [b.w * b.d + b.n, b.d];
}

/**
 * The ratio a / b of two beats as reduced positive integers [p, q], or null when it is not a positive finite ratio
 * representable with safe integers.
 */
export function beatRatio(a: Beat, b: Beat): readonly [number, number] | null {
  const [na, da] = frac(a);
  const [nb, db] = frac(b);
  let p = na * db;
  let q = da * nb;
  if (q === 0 || p === 0) return null;
  if (q < 0) {
    p = -p;
    q = -q;
  }
  if (p <= 0) return null;
  const g = gcd(p, q);
  p /= g;
  q /= g;
  if (!Number.isSafeInteger(p) || !Number.isSafeInteger(q)) return null;
  return [p, q];
}

/**
 * Time stretch of a top / bottom edge drag: the dragged edge goes to `edge` (snapped), the opposite edge (or the
 * playhead with Ctrl) stays. Returns the spec, or null when nothing would change or the edge crossed the pivot.
 */
export function stretchSpec(
  box: NoteBox,
  handle: 'top' | 'bottom',
  edge: Beat,
  pivotAt: Beat | null,
): TransformSpec | null {
  const moving = handle === 'top' ? box.t1 : box.t0;
  const pivot = pivotAt ?? (handle === 'top' ? box.t0 : box.t1);
  const oldSpan = Beats.sub(moving, pivot);
  const newSpan = Beats.sub(edge, pivot);
  if (Beats.eq(oldSpan, Beats.ZERO) || Beats.eq(newSpan, Beats.ZERO)) return null;
  const ratio = beatRatio(newSpan, oldSpan);
  if (ratio === null || ratio[0] === ratio[1]) return null;
  return { timeScale: ratio, timePivot: pivot };
}

/** Smallest X scale an edge drag may reach (keeps notes from collapsing onto one X). */
export const MIN_X_SCALE = 0.01;

/**
 * X scale of a left / right edge drag: the dragged edge goes to `edgeX`, the opposite edge (or X 0 with Ctrl)
 * stays. Null when the box has no width on that side or nothing changes.
 */
export function scaleXSpec(
  box: NoteBox,
  handle: 'left' | 'right',
  edgeX: number,
  aroundZero: boolean,
): TransformSpec | null {
  const moving = handle === 'right' ? box.x1 : box.x0;
  const pivot = aroundZero ? 0 : handle === 'right' ? box.x0 : box.x1;
  const oldSpan = moving - pivot;
  if (Math.abs(oldSpan) < 1e-9) return null;
  const scale = Math.max(MIN_X_SCALE, (edgeX - pivot) / oldSpan);
  if (!Number.isFinite(scale) || Math.abs(scale - 1) < 1e-9) return null;
  return { xScale: scale, xPivot: pivot };
}
